import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const COMPONENTS = [["admission", "Admission fee (one-time)"], ["annual", "Annual fee (one-time)"], ["tuition", "Tuition fee (yearly)"], ["activity", "Activity fee (yearly)"], ["transport", "Transport fee (yearly)"], ["other", "Other charges (yearly)"]];
export const KIT_ITEMS = [["kit", "Kit"], ["dress", "Dress"], ["shoes", "Shoes"]];
export const KIT_OPTIONS = [["with_kit", "With Kit"], ["without_kit", "Without Kit"], ["dress_only", "Dress Only"]];
export const kitCharged = (k, opt) => opt === "with_kit" || (opt === "dress_only" && k === "dress");
export const emptyComponents = () => ({ admission: 0, annual: 0, tuition: 0, activity: 0, transport: 0, other: 0, kit: 0, dress: 0, shoes: 0 });
export function feeTotals(components, kitOption, pct, flat = 0) {
  const gross = Object.entries(components).reduce((a, [k, v]) => a + (KIT_ITEMS.some(([x]) => x === k) && !kitCharged(k, kitOption) ? 0 : Number(v || 0)), 0);
  const pctAmt = Math.round(gross * Number(pct || 0)) / 100;
  return { gross, pctAmt, net: gross - pctAmt - flat };
}
const REDUCTIONS = [["discount", "Discount"], ["scholarship", "Scholarship"], ["concession", "Concession"]];
export const FREQ_LABEL = { monthly: "Monthly", quarterly: "Quarterly", half_yearly: "Half-yearly", yearly: "Yearly" };
const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const blank = () => ({
  student_id: "", academic_year: "2025-26", frequency: "quarterly", start_date: new Date().toISOString().slice(0, 10), late_fee: 0, grace_days: 5,
  components: emptyComponents(), discount: 0, scholarship: 0, concession: 0, kit_option: "with_kit", discount_pct: 0, class_fee_id: null,
});

export function KitBlock({ components, kitOption, onChange }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Kit, dress & shoes (one-time)</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {KIT_OPTIONS.map(([k, l]) => (
          <button type="button" key={k} data-testid={`kit-option-${k}`} onClick={() => onChange(components, k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${kitOption === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {KIT_ITEMS.map(([k, l]) => (
          <div key={k} className={`transition-opacity ${kitCharged(k, kitOption) ? "" : "opacity-50"}`}><Label className="text-xs">{l} (₹){!kitCharged(k, kitOption) && " — not charged"}</Label>
            <Input data-testid={`plan-${k}`} type="number" min={0} value={components[k]} onChange={(e) => onChange({ ...components, [k]: e.target.value }, kitOption)} className="mt-1" /></div>
        ))}
      </div>
    </div>
  );
}

export function FeePlanDialog({ open, onClose, students, editing, onSaved }) {
  const [f, setF] = useState(blank());
  const [fromClass, setFromClass] = useState("");
  useEffect(() => {
    if (open) { setF(editing ? { ...blank(), ...editing, components: { ...emptyComponents(), ...editing.components } } : blank()); setFromClass(""); }
  }, [open, editing]);
  async function pickStudent(sid) {
    setF((cur) => ({ ...cur, student_id: sid }));
    const { data } = await api.get(`/fee-mgmt/class-fees/for-student/${sid}`).catch(() => ({ data: null }));
    if (data) {
      setF((cur) => ({ ...cur, academic_year: data.academic_year, frequency: data.frequency, start_date: data.start_date, late_fee: data.late_fee, grace_days: data.grace_days,
        kit_option: data.kit_option, components: { ...emptyComponents(), ...data.components }, class_fee_id: data.id }));
      setFromClass(`${data.class_name} ${data.academic_year}`);
    } else setFromClass("");
  }

  const flat = Number(f.discount || 0) + Number(f.scholarship || 0) + Number(f.concession || 0);
  const { gross, pctAmt, net } = feeTotals(f.components, f.kit_option, f.discount_pct, flat);
  const num = (v) => Number(v) || 0;

  async function save() {
    try {
      await api.post("/fee-mgmt/plans", {
        ...f, late_fee: num(f.late_fee), grace_days: num(f.grace_days), discount: num(f.discount), scholarship: num(f.scholarship), concession: num(f.concession), discount_pct: num(f.discount_pct),
        components: Object.fromEntries(Object.entries(f.components).map(([k, v]) => [k, num(v)])),
      });
      toast.success("Fee plan saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Assign"} student fee plan</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Student</Label>
              <Select value={f.student_id} onValueChange={pickStudent} disabled={!!editing}>
                <SelectTrigger data-testid="plan-student" className="mt-1"><SelectValue placeholder="Choose a student" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.class_name} {s.section} • {s.admission_number}</SelectItem>)}</SelectContent>
              </Select>
              {fromClass && <div data-testid="plan-from-class" className="text-xs text-emerald-700 mt-1">Filled from class default • {fromClass}. Change anything for this student below.</div>}</div>
            <div><Label>Academic year</Label><Input data-testid="plan-year" value={f.academic_year} onChange={(e) => setF({ ...f, academic_year: e.target.value })} className="mt-1" /></div>
            <div><Label>Payment frequency</Label>
              <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
                <SelectTrigger data-testid="plan-frequency" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FREQ_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Fee components</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {COMPONENTS.map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`plan-${k}`} type="number" min={0} value={f.components[k]} onChange={(e) => setF({ ...f, components: { ...f.components, [k]: e.target.value } })} className="mt-1" /></div>
              ))}
            </div>
          </div>
          <KitBlock components={f.components} kitOption={f.kit_option} onChange={(components, kit_option) => setF({ ...f, components, kit_option })} />
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Reductions (per year)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">Discount (%)</Label><Input data-testid="plan-discount-pct" type="number" min={0} max={100} value={f.discount_pct} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} className="mt-1" /></div>
              {REDUCTIONS.map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l} (₹)</Label><Input data-testid={`plan-${k}`} type="number" min={0} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">First due date</Label><Input data-testid="plan-start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Late fee per overdue installment (₹)</Label><Input data-testid="plan-late-fee" type="number" min={0} value={f.late_fee} onChange={(e) => setF({ ...f, late_fee: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Grace days</Label><Input data-testid="plan-grace" type="number" min={0} max={60} value={f.grace_days} onChange={(e) => setF({ ...f, grace_days: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="rounded-xl bg-violet-50 p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span>Gross <b>{inr(gross)}</b></span>{pctAmt > 0 && <span>Discount {Number(f.discount_pct)}% <b className="text-rose-700">−{inr(pctAmt)}</b></span>}<span>Net yearly fee <b className="text-violet-800">{inr(net)}</b></span>
            <span className="text-slate-500">One-time fees, kit, dress and shoes are added to the first installment.</span>
          </div>
          <Button data-testid="plan-save" onClick={save} disabled={!f.student_id || net < 0} className="w-full h-11 bg-violet-600 hover:bg-violet-700">Save fee plan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
